import UserService from "@/services/api/user";

import type { User, GetUsersParams, GetUsersResponse } from "@/types/user";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import type { PayloadAction } from "@reduxjs/toolkit";

interface UserState {
  users: User[];
  totalUsers: number;
  isFetching: boolean;
  profile: User | null;
}

const name = "user";

const initialState: UserState = {
  users: [],
  totalUsers: 0,
  isFetching: false,
  profile: null,
};

export const getMyProfile = createAsyncThunk<User, void, { rejectValue: string }>(
  "user/getMyProfile",
  async (_, { rejectWithValue }) => {
    try {
      return await UserService.getMyProfile();
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch profile");
    }
  }
);

// Async thunk
export const getUsers = createAsyncThunk<
  GetUsersResponse, // return type
  GetUsersParams, // argument type
  { rejectValue: string }
>("user/getUsers", async (params, { rejectWithValue }) => {
  try {
    const data: GetUsersResponse = await UserService.getUsers(params);
    return data ?? { rows: [], count: 0 };
  } catch (err) {
    const error = err as { message?: string };
    return rejectWithValue(error.message ?? "Failed to fetch users");
  }
});
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setProfile: (state, action: PayloadAction<User | null>) => {
      state.profile = action.payload;
    },
    clear: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMyProfile.fulfilled, (state, action: PayloadAction<User>) => {
        state.profile = action.payload;
      })
      .addCase(getUsers.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(
        getUsers.fulfilled,
        (state, action: PayloadAction<GetUsersResponse>) => {
          state.isFetching = false;
          state.users = action.payload.rows;
          state.totalUsers = action.payload.count;
        }
      )
      .addCase(getUsers.rejected, (state, action) => {
        state.isFetching = false;
        console.error("Failed to fetch users:", action.payload);
      });
  },
});

export default userSlice.reducer;
export const { clear, setProfile } = userSlice.actions;
export type { UserState };
